import { getMainPhoto } from '../utils/composite';
// src/pages/ShopView.jsx
// Универсальная витрина магазина — для покупателя и гостя.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  shop as shopApi, products as productsApi,
  categories as categoriesApi, favorites as favApi,
  loyalty as loyaltyApi, cart as cartApi,
} from '../api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import Icon from '../components/Icon';
import { formatMoney } from '../data/currencies';

function unitLabel(u) {
  if (u === 'kg' || u === 'кг') return 'кг';
  if (u === 'l' || u === 'л')   return 'л';
  return 'шт';
}

export default function ShopView() {
  const { t } = useTranslation();
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();
  const { show, ToastView } = useToast();

  const [shop, setShop]               = useState(null);
  const [items, setItems]             = useState([]);
  const [cats, setCats]               = useState([]);
  const [card, setCard]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [isFav, setIsFav]             = useState(false);
  const [activeCat, setActiveCat]     = useState('all');
  const [activeSubCat, setActiveSubCat] = useState(null);
  const [search, setSearch]           = useState('');
  const [sort, setSort]               = useState('new');
  const [showDelivery, setShowDelivery] = useState(false);
  const [cartCount, setCartCount]     = useState(0);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [shopId]);

  useEffect(() => {
    if (shopId) setCartCount(cartApi.count(shopId));
  }, [shopId, items]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [shopData, catalog, catList] = await Promise.all([
        shopApi.getById(shopId),
        productsApi.catalog(shopId).catch(() => []),
        categoriesApi.publicList(shopId).catch(() => []),
      ]);
      setShop(shopData);
      setItems(Array.isArray(catalog) ? catalog : []);
      setCats(Array.isArray(catList) ? catList : []);
      if (user && !isGuest) {
        try {
          const cards = await loyaltyApi.cards();
          setCard((cards || []).find(c => c.shopId === shopId) || null);
        } catch (e) {}
      }
      setIsFav(favApi.has(shopId));
    } catch (e) {
      setError(e.message || t('common.error'));
    }
    setLoading(false);
  };

  const toggleFav = async () => {
    if (isGuest) return show(t('shopView.loginToFavorite'), 'error');
    if (isFav) {
      favApi.remove(shopId);
      setIsFav(false);
      show(t('shopView.removedFromFavs'));
    } else {
      try {
        await favApi.add(shopId);
        setIsFav(true);
        show(t('customerCatalog.shopAdded'));
      } catch (e) { show(e.message, 'error'); }
    }
  };

  const addToCart = (product) => {
    // Если у товара есть варианты — нельзя просто добавить, нужно выбрать на странице товара
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (variants.length > 1) {
      navigate(`/shop/${shopId}/product/${product.id}`);
      return;
    }
    cartApi.add(shopId, product, 1);
    setCartCount(cartApi.count(shopId));
    show(`${product.name} · ${t("productView.addedToCart")}`);
  };

  const rootCats = cats.filter(c => !c.parentId);
  const subCats  = activeCat === 'all' ? [] : cats.filter(c => c.parentId === activeCat);

  let filtered = items;

  if (activeCat !== 'all') {
    if (activeSubCat) {
      filtered = filtered.filter(p => p.categoryId === activeSubCat);
    } else {
      const subIds = subCats.map(s => s.id);
      filtered = filtered.filter(p => p.categoryId === activeCat || subIds.includes(p.categoryId));
    }
  }

  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.article || '').toLowerCase().includes(q)
    );
  }

  filtered = [...filtered];
  if (sort === 'cheap')          filtered.sort((a, b) => (a.salePrice || 0) - (b.salePrice || 0));
  else if (sort === 'expensive') filtered.sort((a, b) => (b.salePrice || 0) - (a.salePrice || 0));

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  if (error || !shop) {
    return (
      <div className="page">
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate(-1)}><Icon name="chevronLeft" size={18} /></button>
          <h1 className="page-title">{t("shopView.shopMode")}</h1>
        </div>
        <div className="empty-state">
          <div className="icon"><Icon name="alert" size={28} stroke={1.5} /></div>
          <p>{error || t('customerCatalog.shopNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {ToastView}

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="page-subtitle">{t("shopView.shopMode")}</p>
          <h1 className="page-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {shop.name}
          </h1>
        </div>

        {!isGuest && (
          <button
            onClick={toggleFav}
            style={{
              width: 36, height: 36, borderRadius: 'var(--r-sm)',
              background: isFav ? 'var(--brand-accent-soft)' : 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              color: isFav ? 'var(--brand-accent)' : 'var(--fg-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <Icon name={isFav ? 'starFilled' : 'star'} size={18} stroke={isFav ? 0 : 2} />
          </button>
        )}

        {shop.cartEnabled && cartCount > 0 && (
          <button
            onClick={() => navigate(`/shop/${shopId}/cart`)}
            style={{
              width: 36, height: 36, borderRadius: 'var(--r-sm)',
              background: 'var(--brand-primary)', border: 'none',
              color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0, position: 'relative',
            }}
          >
            <Icon name="cart" size={18} />
            <span style={{
              position: 'absolute', top: -4, right: -4,
              background: 'var(--brand-accent)', color: '#fff',
              borderRadius: '50%', minWidth: 18, height: 18,
              fontSize: 10, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 4px', border: '2px solid var(--bg)',
              fontVariantNumeric: 'tabular-nums',
            }}>{cartCount}</span>
          </button>
        )}
      </div>

      <div style={{ padding: '0 20px', marginBottom: 12 }}>
        {/* Шапка магазина с фото */}
        {(shop.photo || shop.description || (card && !isGuest)) && (
          <div className="card" style={{ marginBottom: 12, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
            {shop.photo && (
              <img src={shop.photo} alt=""
                style={{ width: 56, height: 56, borderRadius: 'var(--r-sm)', objectFit: 'cover', flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              {shop.description && (
                <p style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
                  {shop.description}
                </p>
              )}
              {card && shop.loyaltyEnabled !== false && (
                <div style={{
                  marginTop: shop.description ? 8 : 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>{t("shopView.yourBalance")}</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-accent)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                    {(card.points || 0).toLocaleString()} ⭐
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Баннер доставки */}
        {shop.deliveryEnabled && shop.deliveryInfo && (
          <button
            onClick={() => setShowDelivery(true)}
            style={{
              width: '100%',
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 14px',
              background: 'var(--brand-accent-soft)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              borderRadius: 'var(--r-md)',
              color: 'var(--brand-accent)',
              fontSize: 14, fontWeight: 600,
              cursor: 'pointer', textAlign: 'left',
              fontFamily: 'var(--font-display)',
              marginBottom: 12,
            }}
          >
            <Icon name="truck" size={20} stroke={2} />
            <span style={{ flex: 1 }}>{t("shopView.delivery")}</span>
            <span style={{ fontSize: 12 }}>{t("shopView.deliveryTerms")}</span>
          </button>
        )}

        {/* Поиск */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          height: 48, padding: '0 16px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          marginBottom: 10,
        }}>
          <Icon name="search" size={18} color="var(--fg-faint)" />
          <input
            placeholder={t("shopView.search")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              all: 'unset', flex: 1, fontSize: 15,
              fontFamily: 'inherit', color: 'var(--fg)',
            }}
          />
        </div>
      </div>

      {/* Чипы корневых категорий */}
      {rootCats.length > 0 && (
        <CategoryChips
          cats={[{ id: 'all', name: t('shopView.allProducts'), photo: null }, ...rootCats]}
          activeId={activeCat}
          onClick={(id) => { setActiveCat(id); setActiveSubCat(null); }}
        />
      )}

      {/* Подкатегории */}
      {subCats.length > 0 && (
        <CategoryChips
          cats={[{ id: null, name: t('common.all'), photo: null }, ...subCats]}
          activeId={activeSubCat}
          onClick={(id) => setActiveSubCat(id)}
          accent
        />
      )}

      {/* Сортировка */}
      <div style={{ padding: '0 20px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ fontSize: 12, color: 'var(--fg-faint)', fontWeight: 600 }}>
          {t('shopView.products', { count: filtered.length })}
        </p>
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          style={{
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-xs)', padding: '6px 10px',
            fontSize: 12, color: 'var(--fg)', cursor: 'pointer',
            fontFamily: 'var(--font-display)',
          }}
        >
          <option value="new">{t("shopView.newest")}</option>
          <option value="cheap">{t("shopView.cheapest")}</option>
          <option value="expensive">{t("shopView.expensive")}</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="icon"><Icon name="package" size={28} stroke={1.5} /></div>
          <p>{t("products.noProductsFound")}</p>
        </div>
      ) : (
        <div className="shop-grid">
          {filtered.map(p => {
            // Цена: если варианты — диапазон, иначе одна
            const activeVariants = Array.isArray(p.variants) ? p.variants : [];
            const hasMultiVariants = activeVariants.length > 1;
            let priceLabel = formatMoney(p.salePrice || 0, shop?.currency);
            if (hasMultiVariants) {
              const prices = activeVariants
                .map(v => Number(v.salePrice) || 0)
                .filter(x => x > 0);
              if (prices.length > 0) {
                const min = Math.min(...prices);
                const max = Math.max(...prices);
                priceLabel = (min === max)
                  ? formatMoney(min, shop?.currency)
                  : `${t('productView.from') || 'от'} ${formatMoney(min, shop?.currency)}`;
              }
            }
            return (
            <div
              key={p.id}
              className="product-card fade-up"
              onClick={() => navigate(`/shop/${shopId}/product/${p.id}`)}
            >
              <div className="photo">
                {getMainPhoto(p) ? <img src={getMainPhoto(p)} alt={p.name} /> : <Icon name="package" size={32} stroke={1.5} />}
              </div>
              <div className="info">
                <div className="price">{priceLabel}</div>
                <div className="name">
                  {p.name}
                  {hasMultiVariants && (
                    <span style={{
                      marginLeft: 6,
                      fontSize: 9, fontWeight: 700,
                      background: 'var(--brand-accent-soft, var(--brand-primary-soft))',
                      color: 'var(--brand-accent, var(--brand-primary))',
                      padding: '1px 5px', borderRadius: 4,
                      verticalAlign: 'middle',
                    }}>
                      {activeVariants.length}
                    </span>
                  )}
                </div>
                {shop.cartEnabled && (
                  <button
                    className="btn btn-soft btn-sm"
                    onClick={(e) => { e.stopPropagation(); addToCart(p); }}
                    style={{ marginTop: 4, padding: '6px 8px', fontSize: 12, minHeight: 32 }}
                  >
                    <Icon name="plus" size={12} stroke={2.5} />
                    {hasMultiVariants ? (t('variants.pickVariant') || t('productView.addToCart')) : t('productView.addToCart')}
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Модалка доставки */}
      {showDelivery && (
        <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowDelivery(false)}>
          <div className="modal-sheet">
            <div className="modal-header">
              <p className="modal-title">
                <Icon name="truck" size={18} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                {t("settings.deliveryInfo")}
              </p>
              <button className="modal-close" onClick={() => setShowDelivery(false)}>
                <Icon name="x" size={16} />
              </button>
            </div>
            <div style={{
              fontSize: 14, color: 'var(--fg)',
              lineHeight: 1.6, whiteSpace: 'pre-wrap',
              padding: '4px 4px 16px',
            }}>
              {shop.deliveryInfo}
            </div>
            {shop.cartEnabled && shop.whatsappNumber && (
              <button
                className="btn btn-whatsapp"
                onClick={() => window.open(`https://wa.me/${shop.whatsappNumber}`, '_blank')}
              >
                <Icon name="whatsapp" size={18} stroke={0} color="currentColor" />
                {t('cart.sendOrder')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Чипы категорий с поддержкой фото
function CategoryChips({ cats, activeId, onClick, accent }) {
  return (
    <div className="chip-row">
      {cats.map((c) => {
        const isActive = activeId === c.id;
        const baseStyle = {};
        if (accent && isActive) {
          baseStyle.background = 'var(--brand-accent)';
          baseStyle.borderColor = 'var(--brand-accent)';
        }
        return (
          <button
            key={c.id ?? 'null'}
            className={`chip ${isActive ? 'active' : ''}`}
            onClick={() => onClick(c.id)}
            style={{
              ...baseStyle,
              padding: c.photo ? '4px 12px 4px 4px' : '8px 16px',
              gap: c.photo ? 8 : 0,
            }}
          >
            {c.photo && (
              <img src={c.photo} alt=""
                style={{
                  width: 28, height: 28, borderRadius: 'var(--r-xs)',
                  objectFit: 'cover', flexShrink: 0,
                }} />
            )}
            <span>{c.name}</span>
          </button>
        );
      })}
    </div>
  );
}
